import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LOGO_IMG } from "@/lib/brand";
import { toast } from "sonner";
import { UploadSimple, CheckCircle, Clock, XCircle, ImageSquare } from "@phosphor-icons/react";

const backend = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u ? `${u.startsWith("http") ? "" : backend}${u}` : null);

const STATUS = {
  LIVE: { cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle, text: "Live on mailer" },
  PENDING: { cls: "bg-amber-100 text-amber-700", icon: Clock, text: "Pending approval" },
  REJECTED: { cls: "bg-red-100 text-red-700", icon: XCircle, text: "Rejected — please re-upload" },
  NONE: { cls: "bg-slate-100 text-slate-500", icon: ImageSquare, text: "No artwork yet" },
};

export default function MerchantCreative() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [warns, setWarns] = useState({});

  const checkPrint = (file) => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const w = img.naturalWidth, h = img.naturalHeight;
      const issues = [];
      if (Math.min(w, h) < 800) issues.push(`low resolution (${w}×${h}px — aim for 1000px+ for crisp print)`);
      const ar = w / h;
      if (ar < 0.4 || ar > 2.6) issues.push("unusual aspect ratio for a postcard block");
      resolve(issues.join("; "));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(""); };
    img.src = url;
  });

  const load = () => api.get(`/creative/${token}`).then(({ data }) => setData(data))
    .catch(() => setError("This creative link is invalid or has expired."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const upload = async (categoryId, file) => {
    if (!file) return;
    setBusy(categoryId);
    try {
      const warn = await checkPrint(file);
      setWarns((w) => ({ ...w, [categoryId]: warn }));
      if (warn) toast.warning("Print-ready check: " + warn);
      const fd = new FormData(); fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/creative/${token}/artwork`, { categoryId, fileId: up.file_id });
      toast.success("Artwork submitted for approval");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 p-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><img src={LOGO_IMG} alt="PMI" className="w-40 sm:w-[220px] h-auto" /></Link>
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Creative Studio</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10" data-testid="creative-page">
        {error && (
          <div className="bg-white border border-red-200 p-10 text-center" data-testid="creative-error">
            <XCircle size={48} weight="fill" className="mx-auto text-red-400" />
            <h1 className="font-display text-2xl mt-4">{error}</h1>
            <p className="text-sm text-slate-500 mt-2">Check the link in your confirmation email or contact your PMI rep.</p>
          </div>
        )}

        {!error && data && (
          <>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Your postcard artwork</p>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-1">{data.business || "Your business"}</h1>
            <p className="text-slate-500 mt-2">Zip <span className="font-[600] text-navy-900">{data.zipCode}</span> · Upload or replace the creative for each block you purchased. Every design is reviewed by our team before it goes to print.</p>

            <div className="grid sm:grid-cols-2 gap-5 mt-8">
              {data.categories.map((c) => {
                const st = STATUS[c.status] || STATUS.NONE;
                const StIcon = st.icon;
                const preview = c.status === "PENDING" ? c.pendingArtworkUrl : c.liveArtworkUrl;
                return (
                  <div key={c.categoryId} data-testid={`creative-cat-${c.categoryId}`} className="bg-white border border-slate-200 p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-display text-xl tracking-tight text-navy-900">{c.name}</p>
                        <p className="text-[11px] uppercase tracking-wider text-slate-400">{c.kind === "FULL" ? "Full Slot" : "Half Slot"}</p>
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 flex items-center gap-1 ${st.cls}`} data-testid={`creative-status-${c.categoryId}`}>
                        <StIcon size={12} weight="fill" /> {st.text}
                      </span>
                    </div>
                    <div className="mt-4 border border-slate-200 bg-slate-50 aspect-video flex items-center justify-center overflow-hidden">
                      {abs(preview) ? <img src={abs(preview)} alt={c.name} className="max-h-full max-w-full object-contain" data-testid={`creative-img-${c.categoryId}`} />
                        : <span className="text-xs text-slate-400 flex flex-col items-center gap-1"><ImageSquare size={26} /> No artwork uploaded</span>}
                    </div>
                    <label data-testid={`creative-upload-${c.categoryId}`}
                      className="mt-4 border border-dashed border-slate-300 hover:border-navy-900 px-4 py-3 text-sm font-[600] text-slate-600 cursor-pointer flex items-center justify-center gap-2 transition-colors">
                      <UploadSimple size={16} />
                      {busy === c.categoryId ? "Uploading…" : c.status === "NONE" ? "Upload artwork" : "Replace artwork"}
                      <input type="file" accept="image/*" className="hidden" disabled={!!busy}
                        onChange={(e) => upload(c.categoryId, e.target.files[0])} />
                    </label>
                    {c.status === "PENDING" && <p className="text-[11px] text-amber-600 mt-2">Submitted — awaiting PMI approval before it appears on the live mailer.</p>}
                    {c.status === "REJECTED" && (
                      <p className="text-[11px] text-red-600 mt-2" data-testid={`creative-rejection-${c.categoryId}`}>
                        Rejected{c.rejectionNote ? `: ${c.rejectionNote}` : " — please upload a new version."}
                      </p>
                    )}
                    {warns[c.categoryId] && (
                      <p className="text-[11px] text-amber-600 mt-2" data-testid={`print-warn-${c.categoryId}`}>
                        ⚠ Print-ready heads up: {warns[c.categoryId]}. It was still submitted — replace it if you have a higher-quality file.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
